import { Component, OnInit, Output, SimpleChanges } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import {
  FormGroup,
  FormControl,
  Validators,
  AbstractControl,
} from "@angular/forms";
import { Header, Ruleset, Node, Rule } from "@aptsi-types";
import { RulesetService } from "../ruleset.service";
import { MatDialog } from "@angular/material/dialog";
import { Inject } from "@angular/core";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { ErrorDialogComponent } from "../../shared/dialogs/error-dialog.component";
import { NodeService } from "../node/node.service";
import { DeleteDialogComponent } from "../../shared/dialogs/delete-dialog/delete-dialog.component";
import { SuccessDialogComponent } from "../../shared/dialogs/success-dialog/success-dialog.component";
import { forkJoin, BehaviorSubject } from "rxjs";
import { NodePopupComponent } from "../node-popup/node-popup.component";
import { KeycloakStateService } from 'src/app/core/service/utility.service';
import { EnvironmentService } from "src/app/core/service/environment.service";

@Component({
  selector: "app-ruleset-create-or-update",
  templateUrl: "./ruleset-create-or-update.component.html",
  styleUrls: ["./ruleset-create-or-update.component.scss"],
})
export class RulesetCreateOrUpdateComponent implements OnInit {
  // --------------------------------------------
  // SECTION: UI and State Flags
  // --------------------------------------------
  isCreate = true;
  isNext = false;
  isEdit = false;
  selectedTab: "ruleset" | "node" = "ruleset";

  // --------------------------------------------
  // SECTION: Identifiers and Form
  // --------------------------------------------
  existingRulesetId: string = null;
  ruleset: Ruleset = null;
  form: FormGroup;

  // --------------------------------------------
  // SECTION: Data - Nodes, Ruleset, Headers
  // --------------------------------------------
  nodes: Node[] = [];
  // nodeUpdates: Node[] = [];
  nodeEditUpdates: Node[] = [];
  selectedEntries: any[] = [];
  organizationId: string;

  readonly typeOptions = [
    "risk_score",
    "asset_access_threshold",
    "asset_access_determination",
    "None",
  ];

  headers: Header[] = [
    {
      name: "Rule Id",
      property: "ruleId",
      description: "Unique identifier for the rule",
    },
    {
      name: "Rule Name",
      property: "ruleName",
      description: "Name of the Rule",
    },
    {
      name: "Rule Type",
      property: "ruleType",
      description: "Type of the Rule",
    },
  ];

  filteredHeaders: Header[];

  // --------------------------------------------
  // SECTION: Constructor
  // --------------------------------------------
  constructor(
    private route: ActivatedRoute,
    private rulesetService: RulesetService,
    private router: Router,
    private dialog: MatDialog,
    private nodeService: NodeService,
    private keycloakStateService: KeycloakStateService,
    public environmentService: EnvironmentService 
  ) { }

  // --------------------------------------------
  // SECTION: Lifecycle Hook
  // --------------------------------------------
  async ngOnInit(): Promise<void> {
    this.organizationId = this.keycloakStateService.getOrganizationId(); // <-- Retrieve here
    console.log('Organization ID in component:', this.organizationId);
    this.isCreate = Boolean(this.route.snapshot.data.create);
    this.existingRulesetId = this.route.snapshot.params.rulesetId;

    if (!this.isCreate) {
      this.fetchRulesets();
      this.fetchNode();
      this.isNext = true;
    }

    this.initForm();


    if (this.isCreate) {
      this.rulesetService.nodes$.subscribe((entries) => {
        this.nodes = entries;
      });
    } else if (!this.isCreate) {
      this.rulesetService.nodes$.subscribe((entries) => {
        this.nodeEditUpdates = entries;
        console.log("Node Edit Updates:", this.nodeEditUpdates);
      });
    }
  }

  // --------------------------------------------
  // SECTION: Form Initialization & Validators
  // --------------------------------------------
  initForm() {

    this.form = this.isCreate
      ? new FormGroup({
        rulesetName: new FormControl(null, Validators.required),
        rulesetType: new FormControl(this.ruleset?.rulesetType || "None", [
          Validators.required,
          this.typeValidator,
        ]),
      })
      : new FormGroup({
        rulesetName: new FormControl(
          this.ruleset?.name || null,
          Validators.required
        ),
        rulesetId: new FormControl(
          this.isCreate ? null : this.existingRulesetId,
          Validators.required
        ),
        rulesetType: new FormControl("None", [
          Validators.required,
          this.typeValidator,
        ]),
      });
  }

  typeValidator(control: AbstractControl): { [key: string]: any } | null {
    const selectedType = control.value;
    return selectedType === "None" ? { noneSelected: true } : null;
  }

  // --------------------------------------------
  // SECTION: Navigation
  // --------------------------------------------
  cancel() {
    this.router.navigate([
      "ada-access/auth-tools/policy-authoring/ruleset/search",
    ]);
  }

  redirectToNodeCreate() {
    this.dialog.open(NodePopupComponent, {
      data: {
        title: "Node Deleted Successfully",
        message: "The Node has been deleted successfully.",
      },
    });
  }

  // --------------------------------------------
  // SECTION: Data Fetching
  // --------------------------------------------
  async fetchRulesets() {
    try {
      const response = await this.rulesetService
        .getRuleset(this.existingRulesetId)
        .toPromise();
      this.ruleset = response["Resource retrieved successfully"]?.[0] || null;

      if (this.ruleset) {
        this.form.patchValue({
          rulesetName: this.ruleset.name,
          rulesetType: this.ruleset.rulesetType,
        });
      }
    } catch (error) {
      console.error("Error fetching rulesets:", error);
    }
  }

  async fetchNode() {
    try {
      const response = await this.rulesetService
        .getNode(this.existingRulesetId)
        .toPromise();
      this.nodeEditUpdates = response["nodes"];
      console.log("Fetched Nodes:", this.nodeEditUpdates);

      // 🔥 This line is required for addEntry() to work
      this.rulesetService.nodes.next([...this.nodeEditUpdates]);

    } catch (error) {
      console.error("Error fetching nodes:", error);
    }
  }


  // --------------------------------------------
  // SECTION: Submit Logic
  // --------------------------------------------
  async submit() {
    try {
      this.form.get("rulesetId")?.setErrors(null);

      if (this.isCreate) {
        const exists = await this.checkRulesetIdExists(
          this.form.value.rulesetId
        );
        if (exists) {
          this.form.get("rulesetId")?.setErrors({ rulesetIdExists: true });
          return;
        }

        const response = await this.rulesetService
          .createRuleset(
            this.form.value.rulesetName,
            this.form.value.rulesetType,
            this.organizationId
          )
          .toPromise();

        this.ruleset = response["Resource created successfully"]?.[0] || null;
        this.existingRulesetId = this.ruleset?.rulesetid || null;

        this.isNext = true;
        this.selectedTab = "node";
      } else {
        await this.rulesetService
          .updateRuleset(
            this.existingRulesetId,
            this.form.value.rulesetName,
            this.form.value.rulesetType,
            this.organizationId
          )
          .toPromise();

        this.saveNodes();
      }
    } catch (error) {
      this.openErrorDialog(error.message || "An unexpected error occurred.");
    }
  }

  async checkRulesetIdExists(rulesetId: string): Promise<boolean> {
    try {
      const response = await this.rulesetService
        .getRuleset(rulesetId)
        .toPromise();
      return !!response["Resource retrieved successfully."]?.length;
    } catch (error) {
      console.error("Error checking rulesetId existence:", error);
      return false;
    }
  }

  // --------------------------------------------
  // SECTION: Node Actions
  // --------------------------------------------
async addNode(datapoint: any) {
  const nodeId = datapoint.ruleId;

  this.dialog.open(NodePopupComponent, {
    data: {
      title: "Node Added Successfully",
      message: "The Node has been added successfully.",
      parentid: nodeId,
      selectedEntries: this.isCreate ? this.nodes : this.nodeEditUpdates,
    },
  });
}

  async deleteNode(datapoint: any) {
    try {
      const dialogRef = this.dialog.open(DeleteDialogComponent, {
        data: {
          title: "Confirm Delete",
          message: "Are you sure you want to delete this node?",
          isConfirm: false,
        },
      });

      const result = await dialogRef.afterClosed().toPromise();

      if (result) {
        if (this.isCreate) {
          this.nodes = this.removeNodeRecursively(this.nodes, datapoint.ruleId);
          this.rulesetService.nodes.next([...this.nodes]);
        } else {
          // if (this.isEdit) {
          this.nodeEditUpdates = this.removeNodeRecursively(
            this.nodeEditUpdates,
            datapoint.ruleId
          );
          this.rulesetService.nodes.next([...this.nodeEditUpdates]);
          // } 
          // else {
          //   await this.rulesetService
          //     .deleteNode(datapoint.ruleId, this.existingRulesetId)
          //     .toPromise();
          //   this.fetchNode();
          // }
        }

        this.dialog.open(DeleteDialogComponent, {
          data: {
            title: "Success",
            message: "The Node has been deleted successfully.",
            isConfirm: true,
          },
        });
      }
    } catch (error) {
      console.error("Error deleting Node:", error);
    }
  }

  private removeNodeRecursively(nodes: Node[], nodeId: string): Node[] {
    return nodes
      .filter((node) => node.ruleId !== nodeId)
      .map((node) => ({
        ...node,
        children: node.children
          ? this.removeNodeRecursively(node.children, nodeId)
          : [],
      }));
  }

  async finish() {
    if (this.isCreate) {
      await this.rulesetService
        .createNodeFlow(this.existingRulesetId, this.nodes)
        .toPromise();
      await this.rulesetService.saveFlow().toPromise();
    }

    this.openSuccessDialog(this.isCreate);
  }

  saveNodes() {

    console.log("Node Edit Updates:", this.nodeEditUpdates);
    this.rulesetService
      .updateNodeFlow(this.existingRulesetId, this.nodeEditUpdates)
      .toPromise();

    this.openSuccessDialog(this.isCreate);
  }

  // --------------------------------------------
  // SECTION: Dialogs
  // --------------------------------------------
  openSuccessDialog(isCreate: boolean): void {
    const dialogRef = this.dialog.open(SuccessDialogComponent, {
      width: "250px",
      data: { isCreate: isCreate, title: "Ruleset" },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.router.navigate([
        "ada-access/auth-tools/policy-authoring/ruleset/search",
      ]);
    });
  }

  openErrorDialog(errorMessage: string): void {
    this.dialog.open(ErrorDialogComponent, {
      width: "250px",
      data: { errorMessage: errorMessage },
    });
  }
}
